<script>
/**
 * Painel do cliente em foco — o que a lateral da `/ia` mostra no lugar do
 * painel de situação do escritório quando a conversa tem um cliente.
 *
 * ★ A troca é de ASSUNTO, não de layout: os nove cards de módulo falam do
 * escritório, e com um cliente na conversa eles passam a ser o dado errado ao
 * lado da pergunta certa. Mesma coluna, mesmo idioma visual (rótulo em caixa
 * alta, divisória hairline, nada de card branco), outro escopo.
 *
 * Componente BURRO — tudo por prop, nenhuma chamada de API. Quem busca é a
 * `view-chat-imersivo`, como já faz com os insights: ela é quem sabe que esta é
 * a única tela onde o painel existe.
 *
 * As seções, na ordem: quem é o cliente, quanto ele tem, o que está pendente,
 * quando falamos por último, o que foi assinado e o que foi gravado. É a ordem
 * de quem abre a conversa querendo se situar.
 *
 * ⚠️ Contratos e transcrições são seções SEPARADAS (cada uma com rótulo, `→`,
 * contador e estado vazio próprios) — andaram fundidas numa só por um tempo, e
 * "Nenhum contrato ou transcrição" escondia qual das duas faltava. Desde
 * 14/09/2026 elas dividem UMA LINHA, em duas colunas: são as duas seções mais
 * curtas do painel (uma linha por item, sem gráfico), e lado a lado custam a
 * altura de uma só. É o que fecha o orçamento de altura — ver o `<style>`.
 */
import IaClienteCabecalho from './ia-cliente-cabecalho.vue'
import IaClienteCarteira from './ia-cliente-carteira.vue'
import IaClienteTarefas from './ia-cliente-tarefas.vue'
import IaClienteContatos from './ia-cliente-contatos.vue'
import IaClienteContratos from './ia-cliente-contratos.vue'
import IaClienteTranscricoes from './ia-cliente-transcricoes.vue'

export default {
  name: 'IaClientePainel',
  components: {
    IaClienteCabecalho,
    IaClienteCarteira,
    IaClienteTarefas,
    IaClienteContatos,
    IaClienteContratos,
    IaClienteTranscricoes,
  },
  props: {
    // `{ name, id, perfil }` — o shape de `user/allClients`.
    cliente: { type: Object, required: true },
    // Ativos do cliente, JÁ filtrados pela view.
    ativos: { type: Array, default: () => [] },
    serie: { type: Array, default: () => [] },
    variacao: { type: Number, default: null },
    // `response.value.grupos` do CRM, cru.
    gruposDeTarefa: { type: Array, default: () => [] },
    // `'lead'` | `'cliente'` | `''`, já resolvido pela view.
    modo: { type: String, default: '' },
    // `value` da timeline do CRM, cru.
    timeline: { type: Object, default: null },
    // `documents/documentsByClient`, cru.
    contratos: { type: Array, default: () => [] },
    // `value` de `apiCrmTranscricoes.listar`, cru.
    transcricoes: { type: Array, default: () => [] },
    // Um só para o painel inteiro: as três chamadas partem juntas e a coluna
    // deve assentar de uma vez. Esqueleto por seção faria o bloco montar em
    // três tempos, e cada assentamento empurra o que está abaixo.
    carregando: { type: Boolean, default: false },
  },
}
</script>

<template>
  <div class="ia-cliente-painel">
    <IaClienteCabecalho
      class="ia-cliente-painel__bloco py-4"
      navegavel
      :cliente="cliente"
      :modo="modo"
      @abrir="$emit('abrir-perfil', cliente)"
    />

    <IaClienteCarteira
      class="ia-cliente-painel__bloco py-4"
      navegavel
      :ativos="ativos"
      :serie="serie"
      :variacao="variacao"
      :carregando="carregando"
      @abrir="$emit('abrir-carteira', cliente)"
    />

    <IaClienteTarefas
      class="ia-cliente-painel__bloco py-4"
      navegavel
      :grupos="gruposDeTarefa"
      :carregando="carregando"
      @abrir="$emit('abrir-tarefas', cliente)"
    />

    <IaClienteContatos
      class="ia-cliente-painel__bloco py-4"
      navegavel
      :timeline="timeline"
      :carregando="carregando"
      @abrir="$emit('abrir-contatos', cliente)"
    />

    <!-- Mesma anatomia da linha compacta do painel do escritório (COMPLIANCE ·
         HUB AAWZ · BACKOFFICE): a linha carrega o respiro do bloco, as colunas
         só o conteúdo. -->
    <div class="ia-cliente-painel__bloco ia-cliente-painel__linha py-4">
      <IaClienteContratos
        class="ia-cliente-painel__coluna"
        navegavel
        :contratos="contratos"
        :carregando="carregando"
        @abrir="$emit('abrir-contratos', cliente)"
      />

      <IaClienteTranscricoes
        class="ia-cliente-painel__coluna"
        navegavel
        :transcricoes="transcricoes"
        :carregando="carregando"
        @abrir="$emit('abrir-transcricoes', cliente)"
      />
    </div>
  </div>
</template>

<style lang="sass" scoped>
// ★ Teto de altura do painel (14/09/2026). O bloco superior da lateral alterna
// entre o painel de insights do escritório e este — e o `chat-sidebar.vue` dá
// a ele um PISO (`min-height`) para "Recentes" nascer sempre no mesmo lugar.
// O painel do escritório cabe no piso; este passava dele com cliente cheio e
// empurrava "Recentes" para baixo. Agora o pior caso cabe, e este teto é só a
// rede para o imprevisto — NADA deve ser cortado por ele.
//
// ⚠️ MESMO valor de `$altura-minima-painel-superior` no `chat-sidebar.vue` —
// variável SASS não atravessa `<style scoped>`, então é declarada duas vezes.
// Mude junto com `chat-sidebar.vue` (o teste do painel lê os dois fontes).
$altura-minima-painel-superior: 660px
// A hairline que fecha o `.chat-sidebar__insights` é desenhada por FORA do
// painel e entra nos 660px do wrapper (`box-sizing: border-box`).
$hairline-do-bloco: 1px

// ★ Orçamento do PIOR caso (todas as listas no teto E o nome do cliente em
// DUAS linhas — o teto do `ia-cliente-cabecalho__nome`, `-webkit-line-clamp: 2`,
// 2 × 24px), com `line-height` raiz 1 (caixa de texto = tamanho da fonte,
// salvo `line-height` explícito). O respiro de 16+16 é o `py-4` do template.
//
// | bloco                                   | conteúdo | +respiro | total |
// | cabeçalho (rótulo 28 + 4 + nome 2×24 + 4 + detalhe 12) |   96 | 16+16+1 |   129 |
// | carteira (hero 46 + 4 + alocação 29,4 + 4 + curva 22) | 105,4 | 16+16+1 | 138,4 |
// | tarefas, 2 itens (12 + 10 + 2×30,55 + 10)  |   93,1 | 16+16+1 | 126,1 |
// | contatos, 2 itens (12 + 10 + 2×30,55 + 12) |   95,1 | 16+16+1 | 128,1 |
// | linha de 2 colunas — a mais alta é transcrições, 2 itens (12 + 10 + 2×34,2 + 10) | 100,4 | 16+16 | 132,4 |
// | **total**                               |          |          | **654** |
//
// 654 ≤ 659 (piso 660 − hairline 1): folga de 5px. Com o nome em 1 linha o
// total é 630 (folga 29). Antes (6 blocos, teto 3, resumo do contato em 2
// linhas, respiro 20): 1009px no pior caso. Sem o clamp, um nome em 3 linhas
// daria 678 — e o `overflow: hidden` abaixo cortaria a última linha de
// transcrições sem aviso (review do PR #2261, I-21).
.ia-cliente-painel
  display: flex
  flex-direction: column
  max-height: $altura-minima-painel-superior - $hairline-do-bloco
  overflow: hidden
  // O `overflow: hidden` cortaria o anel de foco (2px + 2px de afastamento)
  // dos rótulos-botão nas bordas laterais: o painel avança 4px sobre o padding
  // do `.chat-sidebar__insights` e devolve os mesmos 4px por dentro — o
  // conteúdo não sai do lugar e o anel tem onde caber.
  margin: 0 -4px
  padding: 0 4px

// ⚠️ O respiro e a divisória entre blocos seguem o painel de módulos, e de
// propósito: os dois ocupam o mesmo lugar da coluna e alternam entre si. Um
// ritmo próprio aqui faria a troca parecer que a lateral trocou de página.
//
// [histórico] Era 14px — lia como compactado demais (divisores colados no
// conteúdo). Subiu para 20. Desde 14/09/2026 é 16: foi a alavanca que fechou o
// orçamento acima sem apertar os gaps internos dos blocos (5 blocos × 8px =
// 40px); ainda acima dos 14 rejeitados, e o item continua mais perto do seu
// rótulo do que do bloco vizinho.
//
// ⚠️ O primeiro bloco MANTÉM o padding de cima. Zerá-lo colava o nome do
// cliente na barra do topo — o painel de módulos não faz isso, e a coluna
// passava a ter dois respiros diferentes conforme o assunto.
//
// O respiro vertical é utility (`py-4` no template, 16px); aqui só a
// divisória, que não tem utility com cor de token.
.ia-cliente-painel__bloco
  border-bottom: 1px solid var(--sidebar-warm-border)

  // A última divisória sairia logo acima da divisória do bloco inteiro, que a
  // lateral já desenha — duas linhas coladas leem como falha de renderização.
  &:last-child
    border-bottom: none

// Receita da `.chat-sidebar__insight-linha--grade`: duas colunas iguais, 16px
// de vale — o mesmo da fileira compacta do painel do escritório.
.ia-cliente-painel__linha
  display: grid
  grid-template-columns: repeat(2, 1fr)
  gap: 16px

// ⚠️ Item de grade nasce com `min-width: auto`: o nome do contrato/reunião,
// que é `nowrap` + reticências, empurraria a coluna em vez de truncar.
.ia-cliente-painel__coluna
  min-width: 0
</style>
